import { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getEventById, createEvent, updateEvent, getAvailableEventRoutes } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { I18nContext } from "../context/I18nContext";

export default function AddEditEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { languageCode } = useContext(I18nContext);

  // تعريف الحقول بناءً على موديل الباك اند
  const [formData, setFormData] = useState({
    event_name: "",
    route_path: "",
    method: "POST",
    trigger_type: "AFTER",
    is_enabled: true,
    description: ""
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Every route this app's own backend currently has registered — see
  // ListAvailableRoutesHandler on the backend — so "API Route Path" below
  // can be picked from a real, always-current list instead of typed from
  // memory. This is purely a convenience autofill: it sets BOTH route_path
  // and method when an option is picked, but the text field underneath
  // stays hand-editable afterward — turning one exact picked path into a
  // wildcard rule (e.g. "/api/orders/:id" -> "/api/orders/*", to watch
  // every order route at once) still has to be typed by hand, since
  // SmartEventLogger matches route_path as a SQL LIKE pattern against the
  // actual request path, not against Fiber's own registered path syntax.
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  useEffect(() => {
    getAvailableEventRoutes(token)
      .then((rows) => setAvailableRoutes(rows || []))
      .catch(() => notify.error("Failed to load the list of available routes"))
      .finally(() => setLoadingRoutes(false));
  }, [token]);

  const routeOptions = useMemo(
    () => availableRoutes.map((r) => ({ value: `${r.method} ${r.path}`, label: `${r.method}  ${r.path}`, method: r.method, path: r.path })),
    [availableRoutes]
  );

  // Not bound to formData directly — after picking, the path is still
  // free to hand-edit (see the wildcard note above), at which point it no
  // longer matches any option here exactly, and that's fine: this select
  // just goes back to showing empty, it isn't the field actually submitted.
  const [pickedRouteValue, setPickedRouteValue] = useState(null);

  useEffect(() => {
    if (id) {
      getEventById(token, id, languageCode)
        .then(data => {
          setFormData({
            event_name: data.event_name,
            route_path: data.route_path,
            method: data.method,
            trigger_type: data.trigger_type,
            is_enabled: data.is_enabled,
            description: data.description || ""
          });
        })
        .catch(() => notify.error("Failed to load event configuration"));
    }
  }, [id, token, languageCode]);

  const handleSubmit = async () => {
    setErrors({});
    
    // 1. التحقق الأمامي (Validation)
    if (!formData.event_name.trim() || !formData.route_path.trim()) {
      notify.error("Please fill in the required fields correctly");
      setErrors({
        event_name: !formData.event_name.trim() ? "Event name is required" : null,
        route_path: !formData.route_path.trim() ? "Route path is required" : null
      });
      return;
    }

    setLoading(true);

    try {
      if (id) {
        await updateEvent(token, id, formData);
        notify.success("Event configuration updated successfully");
      } else {
        await createEvent(token, formData);
        notify.success("Event configuration created successfully");
      }
      navigate("/events");
    } catch (err) {
      const errorMsg = err.data?.error || err.message || "Something went wrong";
      
      if (errorMsg.toLowerCase().includes("name")) {
        setErrors({ event_name: errorMsg });
      }
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button
          onClick={() => navigate("/events")}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
        >
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">
            {id ? "Edit Event Config" : "New Activity Rule"}
          </h2>
          <p className="text-gray-400 text-sm italic font-medium">Define how system actions are audited</p>
        </div>
      </PageHeader>

      {/* Form Card */}
      <div className="bg-white p-6 md:p-10 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <FloatingInput
              label="Event Name (e.g. USER_LOGIN)"
              value={formData.event_name}
              onChange={(e) => {
                setFormData({...formData, event_name: e.target.value.toUpperCase()});
                if (errors.event_name) setErrors({...errors, event_name: null});
              }}
              error={errors.event_name}
              icon={ICONS.EditUser}
            />

            <EntityTranslationsButton entityType="event" entityId={id} defaultName={formData.event_name} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FloatingInput
              label="API Route Path"
              value={formData.route_path}
              onChange={(e) => {
                setFormData({...formData, route_path: e.target.value});
                if (errors.route_path) setErrors({...errors, route_path: null});
              }}
              error={errors.route_path}
              icon={ICONS.Plus}
            />

            <SelectBox
              label="Pick From Existing Routes (optional)"
              name="route_picker"
              options={routeOptions}
              value={pickedRouteValue}
              isDisabled={loadingRoutes}
              placeholder={loadingRoutes ? "Loading routes…" : "Search a route…"}
              onChange={(val) => {
                setPickedRouteValue(val);
                const picked = routeOptions.find((o) => o.value === val);
                if (picked) {
                  setFormData({ ...formData, route_path: picked.path, method: picked.method });
                  setErrors({ ...errors, route_path: null });
                }
              }}
            />
          </div>

          <p className="text-[11px] text-gray-400 -mt-4 ms-1">
            Picking a route above fills in the path and method below — you can still hand-edit the path afterward
            (e.g. turn "/api/orders/:id" into "/api/orders/*") to watch a whole group of routes at once.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">HTTP Method</label>
              <select 
                value={formData.method}
                onChange={(e) => setFormData({...formData, method: e.target.value})}
                className="w-full p-4 rounded-2xl bg-gray-50 border border-transparent focus:border-[var(--primary-color)] transition-all outline-none text-sm font-bold text-gray-700"
              >
                {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">Trigger Logic</label>
              <select 
                value={formData.trigger_type}
                onChange={(e) => setFormData({...formData, trigger_type: e.target.value})}
                className="w-full p-4 rounded-2xl bg-gray-50 border border-transparent focus:border-[var(--primary-color)] transition-all outline-none text-sm font-bold text-gray-700"
              >
                <option value="BEFORE">BEFORE (Audit on attempt)</option>
                <option value="AFTER">AFTER (Audit on success)</option>
              </select>
            </div>
          </div>

          <FloatingInput 
            label="Description (Optional)" 
            value={formData.description} 
            onChange={(e) => setFormData({...formData, description: e.target.value})} 
            icon={ICONS.Plus}
          />

          <div className="p-5 rounded-[1.5rem] bg-gray-50 border border-gray-100 flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">Enable Event Tracking</span>
              <p className="text-[10px] text-gray-400 italic">Toggle whether this action should be logged</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={formData.is_enabled}
                onChange={(e) => setFormData({...formData, is_enabled: e.target.checked})}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? "Update Configuration" : "Create Event Rule"}
            </Button>
            
            <Button 
              variant="secondary" 
              onClick={() => navigate("/events")}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}